// Voice engine: full-mesh WebRTC (one RTCPeerConnection per remote participant), Opus audio, signaling over the gateway.
//
//  mic ─ getUserMedia (AEC / NS / AGC in the browser) ─ WebAudio gain ─ analyser (VAD, meter) ─ MediaStreamDestination ─▶ every peer
//  peer ─ remote track ─ (muted <audio> to keep Chrome pulling it) ─ WebAudio source ─ per-user gain ─ analyser ─ master ─▶ output device
//
// Negotiation follows the W3C "perfect negotiation" pattern: either side may offer; on glare the polite peer
// (the one with the larger user id) rolls back. ICE restarts on failure; the gateway seat survives a socket drop for 15 s.
import type { VoiceState } from "@majlis/protocol/types";
import { get, post, ApiError } from "./api.ts";
import { SfuTransport } from "./sfu.ts";

export type VoiceStatus = "idle" | "connecting" | "connected" | "reconnecting";
export type InputMode = "vad" | "ptt";
export interface VoiceSettings {
  inputId: string; outputId: string; cameraId: string; cameraProfile: string; shareProfile: string; shareAudio: boolean; shareMotion: boolean;
  /** Per-user volume of their screen-share audio (independent of their voice). */
  streamVolumes: Record<string, number>;
  inputGain: number; inputMode: InputMode; pttKey: string; pttReleaseMs: number;
  /** VAD threshold in dBFS (−100 … 0). */
  vadDb: number; echoCancellation: boolean; noiseSuppression: boolean; autoGainControl: boolean;
  userVolumes: Record<string, number>; localMutes: Record<string, boolean>;
}
export interface VideoStat { width: number; height: number; fps: number; codec: string | null; impl: string | null; hw: boolean | null; kbps: number | null }
export interface PeerStats {
  state: RTCPeerConnectionState | "new"; rttMs: number | null; lossPct: number | null; sendKbps: number | null; recvKbps: number | null; codec: string | null;
  /** What we receive from this peer (decoded). */
  inCam?: VideoStat | null; inScreen?: VideoStat | null;
}
/** Requested capture/encode profile. Actual resolution and frame rate depend on the camera/screen, the browser and the network. */
export interface VideoProfile { id: string; width: number; height: number; fps: number; maxBitrate: number }
export const VIDEO_PROFILES: VideoProfile[] = [
  { id: "360p30", width: 640, height: 360, fps: 30, maxBitrate: 600_000 },
  { id: "720p30", width: 1280, height: 720, fps: 30, maxBitrate: 1_500_000 },
  { id: "720p60", width: 1280, height: 720, fps: 60, maxBitrate: 2_500_000 },
  { id: "1080p30", width: 1920, height: 1080, fps: 30, maxBitrate: 3_000_000 },
  { id: "1080p60", width: 1920, height: 1080, fps: 60, maxBitrate: 5_000_000 },
  { id: "1440p30", width: 2560, height: 1440, fps: 30, maxBitrate: 6_000_000 },
  { id: "1440p60", width: 2560, height: 1440, fps: 60, maxBitrate: 9_000_000 },
  { id: "2160p30", width: 3840, height: 2160, fps: 30, maxBitrate: 12_000_000 },
  { id: "2160p60", width: 3840, height: 2160, fps: 60, maxBitrate: 20_000_000 },
];
export const profileById = (id: string) => VIDEO_PROFILES.find((p) => p.id === id) ?? VIDEO_PROFILES[1]!;
export interface ShareOptions { profile: string; audio: boolean; /** prefer frame rate over sharpness (games, video) */ motion: boolean }
export interface LocalVideo { stream: MediaStream; profile: string; actual: { width: number; height: number; fps: number } | null; label: string; audio?: boolean; motion?: boolean }
export interface VoiceView {
  status: VoiceStatus; channelId: string | null; serverId: string | null;
  selfMute: boolean; selfDeaf: boolean; transmitting: boolean; micLevel: number; pttDown: boolean;
  speaking: Record<string, boolean>; peers: Record<string, PeerStats>; pingMs: number | null; error: string | null;
  /** Target and current send bitrate (adaptive). */
  bitrate: { target: number; current: number };
  needsGesture: boolean;
  cam: LocalVideo | null; screen: LocalVideo | null;
  /** Remote camera / screen streams by user id (attach to <video>). */
  remote: Record<string, { cam?: MediaStream | null; screen?: MediaStream | null }>;
}

const DEFAULTS: VoiceSettings = {
  inputId: "default", outputId: "default", cameraId: "default", cameraProfile: "720p30", shareProfile: "1080p30", shareAudio: true, shareMotion: false, streamVolumes: {},
  inputGain: 1, inputMode: "vad", pttKey: "KeyV", pttReleaseMs: 200,
  vadDb: -55, echoCancellation: true, noiseSuppression: true, autoGainControl: true, userVolumes: {}, localMutes: {},
};
const KEY = "majlis-voice";
function loadSettings(): VoiceSettings { try { return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? "{}") }; } catch { return { ...DEFAULTS }; } }

type Send = (f: unknown) => void;

interface Peer {
  id: string; pc: RTCPeerConnection; polite: boolean; makingOffer: boolean; ignoreOffer: boolean; pendingCandidates: RTCIceCandidateInit[];
  el: HTMLAudioElement | null; src: MediaStreamAudioSourceNode | null; gain: GainNode; analyser: AnalyserNode;
  sender: RTCRtpSender | null; lastBytes: { sent: number; recv: number; at: number } | null; disconnectedAt: number | null; speakingUntil: number;
  /** Their stream ids for camera / screen (from signaling). */
  media: { cam: string | null; screen: string | null };
  camSender: RTCRtpSender | null; screenSenders: RTCRtpSender[];
  screenAudio: { el: HTMLAudioElement; src: MediaStreamAudioSourceNode; gain: GainNode } | null;
  streams: Map<string, MediaStream>;
}

/** dBFS of an analyser's current time-domain frame. */
function levelDb(a: AnalyserNode, buf: Float32Array<ArrayBuffer>) {
  a.getFloatTimeDomainData(buf);
  let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
  const rms = Math.sqrt(sum / buf.length);
  return rms > 0 ? 20 * Math.log10(rms) : -100;
}

/** Ask Opus for in-band FEC + DTX on what we receive, mono, with a bitrate cap. Only the a=fmtp line for Opus changes. */
export function tuneOpus(sdp: string, maxBitrate: number): string {
  const pt = /a=rtpmap:(\d+) opus\/48000/i.exec(sdp)?.[1];
  if (!pt) return sdp;
  const want: Record<string, string> = { useinbandfec: "1", usedtx: "1", stereo: "0", maxaveragebitrate: String(maxBitrate) };
  return sdp.replace(new RegExp(`a=fmtp:${pt} ([^\\r\\n]*)`), (_m, params: string) => {
    const kv = new Map(params.split(";").filter(Boolean).map((p) => { const [k, v] = p.split("="); return [k!.trim(), (v ?? "").trim()] as [string, string]; }));
    for (const [k, v] of Object.entries(want)) kv.set(k, v);
    return `a=fmtp:${pt} ${[...kv].map(([k, v]) => `${k}=${v}`).join(";")}`;
  });
}

export class VoiceEngine {
  private send: Send;
  private myId = "";
  private transport: "mesh" | "livekit" = "mesh";
  private sfu: SfuTransport | null = null;
  private sfuAudio = new Map<string, { el: HTMLAudioElement; src: MediaStreamAudioSourceNode; gain: GainNode; source: string; id: string }>();
  private generation = 0;
  private cameraGeneration = 0;
  private screenGeneration = 0;
  private sfuSpeaking: Record<string, boolean> = {};
  settings: VoiceSettings = loadSettings();
  view: VoiceView = this.blank();
  private subs = new Set<() => void>();
  private peers = new Map<string, Peer>();
  private states = new Map<string, VoiceState>();  // participants of the current room (incl. me)
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private mic: MediaStream | null = null;
  private micSrc: MediaStreamAudioSourceNode | null = null;
  private micGain: GainNode | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private outTrack: MediaStreamTrack | null = null;
  private iceServers: RTCIceServer[] = [];
  private tick: number | null = null;
  private statsTimer: number | null = null;
  private iceRefreshTimer: number | null = null;
  private vadOpenUntil = 0;
  private pttReleaseTimer: number | null = null;
  private suppressed = false; private serverMute = false; private serverDeaf = false;
  private goodPeriods = 0;
  private buf = new Float32Array(1024);
  /** Called with the next state the server should hold for us. */
  constructor(send: Send) {
    this.send = send;
    window.addEventListener("keydown", this.onKey, true);
    window.addEventListener("keyup", this.onKey, true);
    window.addEventListener("blur", () => this.setPtt(false));
    window.addEventListener("majlis-native-ptt", (event) => {
      if (this.settings.inputMode === "ptt" && this.view.channelId) this.setPtt((event as CustomEvent).detail === true);
    });
    navigator.mediaDevices?.addEventListener?.("devicechange", () => this.emit());
  }
  setMe(id: string) { this.myId = id; }

  // ---------- subscription (useSyncExternalStore) ----------
  subscribe = (fn: () => void) => { this.subs.add(fn); return () => { this.subs.delete(fn); }; };
  getView = () => this.view;
  private emit(patch: Partial<VoiceView> = {}) { this.view = { ...this.view, ...patch }; for (const f of this.subs) f(); }
  private blank(): VoiceView {
    return { status: "idle", channelId: null, serverId: null, selfMute: false, selfDeaf: false, transmitting: false, micLevel: -100, pttDown: false,
      speaking: {}, peers: {}, pingMs: null, error: null, bitrate: { target: 64000, current: 64000 }, needsGesture: false,
      cam: null, screen: null, remote: {} };
  }

  saveSettings(p: Partial<VoiceSettings>) {
    const prev = this.settings; this.settings = { ...prev, ...p };
    try { localStorage.setItem(KEY, JSON.stringify(this.settings)); } catch { /* storage blocked: keep in memory */ }
    if (this.micGain) this.micGain.gain.value = this.settings.inputGain;
    for (const peer of this.peers.values()) this.applyPeerGain(peer);
    this.applySfuGains();
    const constraintsChanged = (["inputId", "echoCancellation", "noiseSuppression", "autoGainControl"] as const).some((k) => prev[k] !== this.settings[k]);
    if (constraintsChanged && this.mic) void this.restartMic().catch((e) => this.emit({ error: String((e as Error).message ?? e) }));
    if (prev.outputId !== this.settings.outputId) void this.applyOutput();
    this.emit();
  }

  // ---------- join / leave ----------
  async join(channelId: string, serverId: string | null, bitrate = 64000, roomNow: () => VoiceState[] = () => []) {
    if (this.view.channelId === channelId && this.view.status !== "idle") return;
    if (this.view.channelId) this.leave();
    const generation = ++this.generation;
    this.emit({ ...this.blank(), status: "connecting", channelId, serverId, bitrate: { target: bitrate, current: bitrate }, selfMute: this.view.selfMute, selfDeaf: this.view.selfDeaf });
    try {
      const config = await get<{ transport: "mesh" | "livekit" }>("/voice/transport").catch((e) => {
        if (e instanceof ApiError && e.status === 404) return { transport: "mesh" as const }; // Phase 3 API compatibility
        throw e;
      });
      if (generation !== this.generation) return;
      this.transport = config.transport;
      const ice = await get<{ iceServers: RTCIceServer[]; ttlSecs: number }>("/voice/ice-servers");
      if (generation !== this.generation) return;
      this.iceServers = ice.iceServers;
      this.scheduleIceRefresh(generation, Math.max(30, (ice.ttlSecs || 3600) - 120) * 1000);
      await this.startAudio();
    } catch (e) {
      if (generation !== this.generation) return;
      this.teardown();
      const err = e as DOMException;
      this.emit({ ...this.blank(), error: err.name === "NotAllowedError" ? "mic_denied" : err.name === "NotFoundError" ? "no_mic" : String(err.message ?? e) });
      return;
    }
    if (generation !== this.generation || this.view.channelId !== channelId) return;
    this.seed(roomNow());
    this.pushState();
    this.tick = window.setInterval(() => this.onTick(), 50);
    this.statsTimer = window.setInterval(() => void this.collectStats(), 2000);
  }
  leave() {
    if (!this.view.channelId) return;
    this.send({ op: "voice_state", d: { channelId: null } });
    this.teardown();
    this.emit({ ...this.blank(), selfMute: this.view.selfMute, selfDeaf: this.view.selfDeaf });
  }
  private teardown() {
    ++this.generation;
    ++this.cameraGeneration; ++this.screenGeneration;
    this.sfu?.close(); this.sfu = null;
    for (const a of this.sfuAudio.values()) { a.src.disconnect(); a.gain.disconnect(); a.el.srcObject = null; }
    this.sfuAudio.clear();
    this.sfuSpeaking = {};
    if (this.pttReleaseTimer) clearTimeout(this.pttReleaseTimer); this.pttReleaseTimer = null;
    if (this.tick) clearInterval(this.tick); if (this.statsTimer) clearInterval(this.statsTimer);
    if (this.iceRefreshTimer) clearTimeout(this.iceRefreshTimer); this.iceRefreshTimer = null;
    this.tick = this.statsTimer = null;
    for (const p of this.peers.values()) this.closePeer(p);
    this.peers.clear(); this.states.clear();
    this.mic?.getTracks().forEach((t) => t.stop()); this.mic = null;
    this.outTrack?.stop(); this.outTrack = null;
    this.view.cam?.stream.getTracks().forEach((t) => t.stop()); this.view.screen?.stream.getTracks().forEach((t) => t.stop());
    void this.ctx?.close().catch(() => {}); this.ctx = null; this.master = null; this.micSrc = null; this.micGain = null; this.micAnalyser = null;
    this.suppressed = this.serverMute = this.serverDeaf = false;
  }
  private scheduleIceRefresh(generation: number, delay: number) {
    if (this.iceRefreshTimer) clearTimeout(this.iceRefreshTimer);
    this.iceRefreshTimer = window.setTimeout(() => {
      void get<{ iceServers: RTCIceServer[]; ttlSecs: number }>("/voice/ice-servers").then((ice) => {
        if (generation !== this.generation) return;
        this.iceServers = ice.iceServers;
        for (const p of this.peers.values()) p.pc.setConfiguration({ ...p.pc.getConfiguration(), iceServers: this.iceServers });
        this.scheduleIceRefresh(generation, Math.max(30, (ice.ttlSecs || 3600) - 120) * 1000);
      }).catch(() => {
        if (generation !== this.generation) return;
        this.emit({ error: "Voice relay credentials could not be refreshed. Retrying in 30 seconds." });
        this.scheduleIceRefresh(generation, 30_000);
      });
    }, delay);
  }
  setMute(v: boolean) { this.emit({ selfMute: v, ...(v ? {} : { selfDeaf: false }) }); this.applyTransmit(); this.applyDeaf(); this.pushState(); }
  setDeaf(v: boolean) { this.emit({ selfDeaf: v, selfMute: v ? true : this.view.selfMute }); this.applyTransmit(); this.applyDeaf(); this.pushState(); }
  /** Browsers start AudioContexts suspended without a user gesture (e.g. after a reload); call from a click. */
  resumeAudio() { void this.ctx?.resume().then(() => this.emit({ needsGesture: false })); }
  private pushState() {
    if (!this.view.channelId) return;
    this.send({ op: "voice_state", d: { channelId: this.view.channelId, selfMute: this.view.selfMute, selfDeaf: this.view.selfDeaf, selfVideo: !!this.view.cam, selfStream: !!this.view.screen } });
  }

  // ---------- camera & screen ----------
  private mediaMap() { return { cam: this.view.cam?.stream.id ?? null, screen: this.view.screen?.stream.id ?? null }; }
  async startCamera(profileId = this.settings.cameraProfile) {
    if (!this.view.channelId || this.view.status === "idle") return;
    const pr = profileById(profileId);
    const dev = this.settings.cameraId !== "default" ? { deviceId: { exact: this.settings.cameraId } } : {};
    const generation = this.generation;
    const capture = ++this.cameraGeneration;
    const stream = await navigator.mediaDevices.getUserMedia({ video: { ...dev, width: { ideal: pr.width }, height: { ideal: pr.height }, frameRate: { ideal: pr.fps } }, audio: false });
    if (generation !== this.generation || capture !== this.cameraGeneration) { stream.getTracks().forEach((t) => t.stop()); return; }
    this.stopCamera();
    const track = stream.getVideoTracks()[0]!;
    track.contentHint = "motion";
    this.view.cam?.stream.getTracks().forEach((t) => t.stop());
    const st = track.getSettings();
    this.emit({ cam: { stream, profile: pr.id, actual: { width: st.width ?? 0, height: st.height ?? 0, fps: Math.round(st.frameRate ?? 0) }, label: track.label } });
    track.addEventListener("ended", () => { if (this.view.cam?.stream === stream) this.stopCamera(); });
    for (const p of this.peers.values()) this.attachCam(p);
    try { await this.publishSfu(); } catch (e) { if (this.view.cam?.stream === stream) this.stopCamera(); throw e; }
    this.announceMedia(); this.pushState();
  }
  stopCamera() {
    ++this.cameraGeneration;
    const cam = this.view.cam; if (!cam) return;
    for (const t of cam.stream.getTracks()) void this.sfu?.unpublish(t).catch((e) => this.emit({ error: String(e) }));
    cam.stream.getTracks().forEach((t) => t.stop());
    this.emit({ cam: null });
    for (const p of this.peers.values()) if (p.camSender) { p.pc.removeTrack(p.camSender); p.camSender = null; }
    this.announceMedia(); this.pushState();
  }
  async startScreen(opts: ShareOptions = { profile: this.settings.shareProfile, audio: this.settings.shareAudio, motion: this.settings.shareMotion }) {
    if (!this.view.channelId || this.view.status === "idle") return;
    const pr = profileById(opts.profile);
    const generation = this.generation;
    const capture = ++this.screenGeneration;
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: { width: { ideal: pr.width }, height: { ideal: pr.height }, frameRate: { ideal: pr.fps, max: pr.fps } },
      audio: opts.audio ? { echoCancellation: false, noiseSuppression: false, autoGainControl: false } : false,
      // Chromium hints: offer windows/screens, allow switching source, include system audio where the OS supports it.
      ...({ selfBrowserSurface: "exclude", surfaceSwitching: "include", systemAudio: opts.audio ? "include" : "exclude", monitorTypeSurfaces: "include" } as object),
    } as DisplayMediaStreamOptions);
    if (generation !== this.generation || capture !== this.screenGeneration) { stream.getTracks().forEach((t) => t.stop()); return; }
    this.stopScreen();
    const v = stream.getVideoTracks()[0]!;
    v.contentHint = opts.motion ? "motion" : "detail";
    this.view.screen?.stream.getTracks().forEach((t) => t.stop());
    const st = v.getSettings();
    this.emit({ screen: { stream, profile: pr.id, actual: { width: st.width ?? 0, height: st.height ?? 0, fps: Math.round(st.frameRate ?? 0) }, label: v.label,
      audio: stream.getAudioTracks().length > 0, motion: opts.motion } });
    v.addEventListener("ended", () => { if (this.view.screen?.stream === stream) this.stopScreen(); });
    for (const p of this.peers.values()) this.attachScreen(p);
    try { await this.publishSfu(); } catch (e) { if (this.view.screen?.stream === stream) this.stopScreen(); throw e; }
    this.announceMedia(); this.pushState();
  }
  stopScreen() {
    ++this.screenGeneration;
    const sc = this.view.screen; if (!sc) return;
    for (const t of sc.stream.getTracks()) void this.sfu?.unpublish(t).catch((e) => this.emit({ error: String(e) }));
    sc.stream.getTracks().forEach((t) => t.stop());
    this.emit({ screen: null });
    for (const p of this.peers.values()) { for (const s of p.screenSenders) p.pc.removeTrack(s); p.screenSenders = []; }
    this.announceMedia(); this.pushState();
  }
  setStreamVolume(userId: string, v: number) {
    this.saveSettings({ streamVolumes: { ...this.settings.streamVolumes, [userId]: v } });
    const sa = this.peers.get(userId)?.screenAudio; if (sa) sa.gain.gain.value = v;
    this.applySfuGains();
  }
  private announceMedia() { for (const p of this.peers.values()) this.signal(p.id, { media: this.mediaMap() }); }
  private attachCam(p: Peer) {
    const cam = this.view.cam; if (!cam || p.camSender) return;
    const track = cam.stream.getVideoTracks()[0]!;
    p.camSender = p.pc.addTrack(track, cam.stream);
    void this.encodeVideo(p.camSender, profileById(cam.profile), "balanced");
  }
  private attachScreen(p: Peer) {
    const sc = this.view.screen; if (!sc || p.screenSenders.length) return;
    for (const t of sc.stream.getTracks()) {
      const s = p.pc.addTrack(t, sc.stream); p.screenSenders.push(s);
      if (t.kind === "video") void this.encodeVideo(s, profileById(sc.profile), sc.motion ? "maintain-framerate" : "maintain-resolution");
    }
  }
  /** Caps for the encoder; the browser's congestion control adapts below these (resolution/fps per degradationPreference). */
  private async encodeVideo(s: RTCRtpSender, pr: VideoProfile, pref: "balanced" | "maintain-framerate" | "maintain-resolution") {
    const params = s.getParameters() as RTCRtpSendParameters & { degradationPreference?: string };
    if (!params.encodings?.length) params.encodings = [{}];
    params.encodings[0]!.maxBitrate = pr.maxBitrate; params.encodings[0]!.maxFramerate = pr.fps;
    params.degradationPreference = pref;
    await s.setParameters(params).catch((e) => console.warn("video params", e));
  }
  private remoteFor(p: Peer) {
    const cam = p.media.cam ? p.streams.get(p.media.cam) ?? null : null;
    const screen = p.media.screen ? p.streams.get(p.media.screen) ?? null : null;
    const cur = this.view.remote[p.id];
    if (cur?.cam === cam && cur?.screen === screen) return;
    this.emit({ remote: { ...this.view.remote, [p.id]: { cam, screen } } });
    // screen-share audio goes through its own gain so it can be set independently of their voice
    const audio = screen?.getAudioTracks()[0];
    if (audio && !p.screenAudio && this.ctx) {
      const el = new Audio(); el.muted = true; el.srcObject = screen!; void el.play().catch(() => {});
      const src = this.ctx.createMediaStreamSource(screen!); const gain = this.ctx.createGain();
      gain.gain.value = this.settings.streamVolumes[p.id] ?? 1; src.connect(gain); gain.connect(this.master!);
      p.screenAudio = { el, src, gain };
    } else if (!audio && p.screenAudio) { p.screenAudio.src.disconnect(); p.screenAudio.gain.disconnect(); p.screenAudio.el.srcObject = null; p.screenAudio = null; }
  }

  // ---------- audio graph ----------
  private constraints(): MediaTrackConstraints {
    const s = this.settings;
    return { deviceId: s.inputId && s.inputId !== "default" ? { exact: s.inputId } : undefined, echoCancellation: s.echoCancellation,
      noiseSuppression: s.noiseSuppression, autoGainControl: s.autoGainControl, channelCount: 1, sampleRate: 48000 };
  }
  private async startAudio() {
    this.ctx = new AudioContext({ latencyHint: "interactive", sampleRate: 48000 });
    if (this.ctx.state === "suspended") { await this.ctx.resume().catch(() => {}); if (this.ctx.state === "suspended") this.emit({ needsGesture: true }); }
    this.master = this.ctx.createGain(); this.master.connect(this.ctx.destination);
    this.micGain = this.ctx.createGain(); this.micGain.gain.value = this.settings.inputGain;
    this.micAnalyser = this.ctx.createAnalyser(); this.micAnalyser.fftSize = 1024;
    const dest = this.ctx.createMediaStreamDestination();
    this.micGain.connect(this.micAnalyser); this.micGain.connect(dest);
    this.outTrack = dest.stream.getAudioTracks()[0]!;
    await this.restartMic();
    await this.applyOutput();
    this.applyDeaf();
  }
  private async restartMic() {
    const ctx = this.ctx;
    const next = await navigator.mediaDevices.getUserMedia({ audio: this.constraints(), video: false });
    if (!ctx || ctx !== this.ctx || ctx.state === "closed") { next.getTracks().forEach((t) => t.stop()); return; }
    this.mic?.getTracks().forEach((t) => t.stop());
    this.micSrc?.disconnect();
    this.mic = next;
    this.micSrc = this.ctx!.createMediaStreamSource(next);
    this.micSrc.connect(this.micGain!);
    next.getAudioTracks()[0]!.addEventListener("ended", () => this.emit({ error: "mic_lost" }));
  }
  private async applyOutput() {
    const id = this.settings.outputId === "default" ? "" : this.settings.outputId;
    const ctx = this.ctx as (AudioContext & { setSinkId?: (id: string) => Promise<void> }) | null;
    try { if (ctx?.setSinkId) await ctx.setSinkId(id); } catch { this.emit({ error: "output_unavailable" }); }
  }
  private applyDeaf() { if (this.master) this.master.gain.value = this.view.selfDeaf || this.serverDeaf ? 0 : 1; }
  private applyPeerGain(p: Peer) {
    const st = this.states.get(p.id);
    // Cooperative enforcement in mesh mode: we never play someone who is server-muted or lacks SPEAK.
    const blocked = !!st && (st.serverMute || st.suppress);
    p.gain.gain.value = blocked || this.settings.localMutes[p.id] ? 0 : this.settings.userVolumes[p.id] ?? 1;
  }

  // ---------- VAD / PTT ----------
  private onKey = (e: KeyboardEvent) => {
    if (this.settings.inputMode !== "ptt" || e.code !== this.settings.pttKey || !this.view.channelId) return;
    const el = e.target as HTMLElement | null;
    if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) && e.type === "keydown") return; // typing
    if (e.repeat) return;
    this.setPtt(e.type === "keydown");
  };
  setPtt(down: boolean) {
    if (this.pttReleaseTimer) { clearTimeout(this.pttReleaseTimer); this.pttReleaseTimer = null; }
    if (down) { this.emit({ pttDown: true }); this.applyTransmit(); return; }
    if (!this.view.pttDown) return;
    this.pttReleaseTimer = window.setTimeout(() => { this.emit({ pttDown: false }); this.applyTransmit(); }, this.settings.pttReleaseMs);
  }
  private wantTransmit(level: number) {
    if (this.view.selfMute || this.suppressed || this.serverMute) return false;
    if (this.settings.inputMode === "ptt") return this.view.pttDown;
    const now = performance.now();
    if (level > this.settings.vadDb) this.vadOpenUntil = now + 300;
    return now < this.vadOpenUntil;
  }
  private applyTransmit(level = this.view.micLevel) {
    const on = this.wantTransmit(level);
    if (this.outTrack && this.outTrack.enabled !== on) this.outTrack.enabled = on;   // disabled track → silence (DTX: ~no packets)
    if (on !== this.view.transmitting) this.emit({ transmitting: on });
  }
  private onTick() {
    if (!this.micAnalyser) return;
    const lvl = levelDb(this.micAnalyser, this.buf);
    this.applyTransmit(lvl);
    const now = performance.now();
    const speaking: Record<string, boolean> = this.transport === "livekit" ? { ...this.sfuSpeaking } : {};
    speaking[this.myId] = this.view.transmitting && lvl > this.settings.vadDb;
    for (const p of this.peers.values()) {
      const st = this.states.get(p.id);
      if (st?.selfMute || st?.serverMute || st?.suppress) { speaking[p.id] = false; continue; }
      if (levelDb(p.analyser, this.buf) > -60) p.speakingUntil = now + 250;
      speaking[p.id] = now < p.speakingUntil;
    }
    const changed = Object.keys(speaking).some((k) => speaking[k] !== this.view.speaking[k]) || Object.keys(this.view.speaking).length !== Object.keys(speaking).length;
    if (changed || Math.abs(lvl - this.view.micLevel) > 1.5) this.emit({ micLevel: lvl, ...(changed ? { speaking } : {}) });
  }

  // ---------- gateway events ----------
  onVoiceState(v: VoiceState & { left?: boolean }) {
    if (!this.view.channelId) return;
    if (v.userId === this.myId) {
      if (v.left) { if (this.view.status !== "idle" && v.channelId === this.view.channelId) { this.teardown(); this.emit({ ...this.blank(), error: "disconnected" }); } return; }
      if (v.channelId !== this.view.channelId) return;
      this.states.set(v.userId, v);
      this.suppressed = v.suppress; this.serverMute = v.serverMute; this.serverDeaf = v.serverDeaf;
      if (v.canVideo === false && this.view.cam) this.stopCamera();
      if (v.canStream === false && this.view.screen) this.stopScreen();
      this.applyTransmit(); this.applyDeaf();
      if (this.view.status !== "connected") {
        if (this.transport === "livekit") { if (!this.sfu) void this.connectSfu(); return; }
        this.emit({ status: "connected" });
        // connect to everyone already in the room
        for (const s of this.roomStates()) if (s.userId !== this.myId) this.ensurePeer(s.userId);
      }
      return;
    }
    if (v.channelId !== this.view.channelId) { if (this.states.has(v.userId) && !v.left) { this.dropPeer(v.userId); } return; }
    if (v.left) { this.dropPeer(v.userId); return; }
    this.states.set(v.userId, v);
    const peer = this.peers.get(v.userId);
    if (peer) this.applyPeerGain(peer);
    else if (this.view.status === "connected" && this.transport === "mesh") this.ensurePeer(v.userId);
  }
  /** Seed the room from the store (READY / channel snapshot). */
  seed(states: VoiceState[]) { for (const s of states) if (s.channelId === this.view.channelId) this.states.set(s.userId, s); }
  private roomStates() { return [...this.states.values()].filter((s) => s.channelId === this.view.channelId); }
  /** New gateway session (server dropped our seat if the gap was > 15 s): re-announce. */
  onReady(states: VoiceState[]) {
    if (!this.view.channelId) return;
    this.states.clear(); this.seed(states);
    for (const id of [...this.peers.keys()]) if (!this.states.has(id)) this.dropPeer(id);
    if (this.transport === "mesh") this.emit({ status: "reconnecting" });
    this.pushState();
  }
  onGatewayError(e: { op: string; code: string; message: string }) {
    if (e.op !== "voice_state" || !this.view.channelId) return;
    this.teardown();
    this.emit({ ...this.blank(), error: e.code === "channel_full" ? "channel_full" : e.code === "forbidden" ? "forbidden" : e.message });
  }
  async onSignal(from: string, data: { description?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit | null; media?: { cam?: string | null; screen?: string | null } }) {
    if (this.transport !== "mesh") return;
    if (!this.view.channelId || this.view.status === "idle") return;
    const p = this.ensurePeer(from);
    if (data.media) { p.media = { cam: data.media.cam ?? null, screen: data.media.screen ?? null }; this.remoteFor(p); }
    try {
      if (data.description) {
        const d = data.description;
        const collision = d.type === "offer" && (p.makingOffer || p.pc.signalingState !== "stable");
        p.ignoreOffer = !p.polite && collision;
        if (p.ignoreOffer) return;
        await p.pc.setRemoteDescription(d);
        for (const c of p.pendingCandidates.splice(0)) await p.pc.addIceCandidate(c).catch(() => {});
        if (d.type === "offer") {
          const answer = await p.pc.createAnswer();
          await p.pc.setLocalDescription({ type: "answer", sdp: tuneOpus(answer.sdp ?? "", this.view.bitrate.target) });
          this.signal(from, { description: p.pc.localDescription!.toJSON(), media: this.mediaMap() });
        }
      } else if (data.candidate) {
        if (!p.pc.remoteDescription) { p.pendingCandidates.push(data.candidate); return; }
        try { await p.pc.addIceCandidate(data.candidate); } catch (e) { if (!p.ignoreOffer) throw e; }
      }
    } catch (e) { console.warn("voice signal", from, e); }
  }
  private signal(to: string, data: unknown) { this.send({ op: "voice_signal", d: { channelId: this.view.channelId, to, data } }); }

  private async connectSfu() {
    const channelId = this.view.channelId; if (!channelId) return;
    const generation = this.generation;
    const sfu = this.sfu = new SfuTransport();
    try {
      const config = await post<{ url: string; token: string }>(`/channels/${channelId}/voice-token`);
      if (generation !== this.generation) return;
      await sfu.connect(config.url, config.token, {
        state: (status) => { if (generation === this.generation) this.emit({ status }); },
        error: (error) => { if (generation === this.generation) { this.leave(); this.emit({ error }); } },
        permissions: () => { if (generation === this.generation) void this.publishSfu().catch((e) => this.emit({ error: String(e) })); },
        admit: async () => { if (generation === this.generation) await post(`/channels/${channelId}/voice-admit`); },
        speakers: (ids) => { this.sfuSpeaking = Object.fromEntries(ids.map((id) => [id, true])); },
        track: (id, source, track, removed) => { if (generation === this.generation) this.onSfuTrack(id, source, track, removed); },
      });
      if (generation !== this.generation) { sfu.close(); return; }
      await this.publishSfu();
    } catch (e) {
      if (generation !== this.generation) return;
      this.leave(); this.emit({ error: String((e as Error).message ?? e) });
    }
  }
  private async publishSfu() {
    const sfu = this.sfu; if (!sfu) return;
    if (this.outTrack) await sfu.publish(this.outTrack, "microphone");
    const cam = this.view.cam;
    if (cam) {
      const profile = profileById(cam.profile);
      await sfu.publish(cam.stream.getVideoTracks()[0]!, "camera", profile.maxBitrate, profile.fps);
    }
    const screen = this.view.screen;
    if (screen) {
      const profile = profileById(screen.profile);
      for (const t of screen.stream.getTracks()) await sfu.publish(t, t.kind === "video" ? "screen_share" : "screen_share_audio", profile.maxBitrate, profile.fps,
        screen.motion ? "maintain-framerate" : "maintain-resolution");
    }
  }
  private onSfuTrack(id: string, source: string, track: MediaStreamTrack, removed: boolean) {
    if (!this.ctx || !this.master) return;
    if (track.kind === "audio") {
      const old = this.sfuAudio.get(track.id);
      if (old) { old.src.disconnect(); old.gain.disconnect(); old.el.srcObject = null; this.sfuAudio.delete(track.id); }
      if (!removed) {
        const stream = new MediaStream([track]); const el = new Audio(); el.muted = true; el.srcObject = stream; void el.play().catch(() => this.emit({ needsGesture: true }));
        const src = this.ctx.createMediaStreamSource(stream), gain = this.ctx.createGain(); src.connect(gain); gain.connect(this.master);
        this.sfuAudio.set(track.id, { el, src, gain, source, id }); this.applySfuGains();
      }
    }
    if (source === "camera" || source === "screen_share") {
      const key = source === "camera" ? "cam" : "screen";
      const current = this.view.remote[id] ?? {};
      if (removed && !current[key]?.getTracks().includes(track)) return;
      this.emit({ remote: { ...this.view.remote, [id]: { ...current, [key]: removed ? null : new MediaStream([track]) } } });
    }
  }
  private applySfuGains() {
    for (const a of this.sfuAudio.values()) a.gain.gain.value = a.source === "screen_share_audio"
      ? this.settings.streamVolumes[a.id] ?? 1 : this.settings.localMutes[a.id] ? 0 : this.settings.userVolumes[a.id] ?? 1;
  }

  // ---------- peers ----------
  private ensurePeer(id: string): Peer {
    const existing = this.peers.get(id); if (existing) return existing;
    const pc = new RTCPeerConnection({ iceServers: this.iceServers, bundlePolicy: "max-bundle", rtcpMuxPolicy: "require" });
    const ctx = this.ctx!;
    const gain = ctx.createGain(); const analyser = ctx.createAnalyser(); analyser.fftSize = 1024;
    gain.connect(analyser); analyser.connect(this.master!);
    const peer: Peer = { id, pc, polite: false, makingOffer: false, ignoreOffer: false, pendingCandidates: [],
      el: null, src: null, gain, analyser, sender: null, lastBytes: null, disconnectedAt: null, speakingUntil: 0,
      media: { cam: null, screen: null }, camSender: null, screenSenders: [], screenAudio: null, streams: new Map() };
    // ids are decimal snowflakes: compare numerically so both sides agree on who is polite
    peer.polite = BigInt(this.myId) > BigInt(id);
    this.peers.set(id, peer);
    this.applyPeerGain(peer);
    peer.sender = pc.addTrack(this.outTrack!, new MediaStream([this.outTrack!]));
    void this.applyBitrate(peer, this.view.bitrate.current);
    this.attachCam(peer); this.attachScreen(peer);
    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        const offer = await pc.createOffer();
        if (pc.signalingState !== "stable") return;
        await pc.setLocalDescription({ type: "offer", sdp: tuneOpus(offer.sdp ?? "", this.view.bitrate.target) });
        this.signal(id, { description: pc.localDescription!.toJSON(), media: this.mediaMap() });
      } catch (e) { console.warn("voice offer", e); } finally { peer.makingOffer = false; }
    };
    pc.onicecandidate = (e) => this.signal(id, { candidate: e.candidate ? e.candidate.toJSON() : null });
    pc.ontrack = (e) => {
      const stream = e.streams[0] ?? new MediaStream([e.track]);
      if (e.track.kind === "video" || stream.id === peer.media.screen || stream.id === peer.media.cam) {
        peer.streams.set(stream.id, stream);
        stream.onremovetrack = () => { if (!stream.getTracks().length) { peer.streams.delete(stream.id); this.remoteFor(peer); } };
        this.remoteFor(peer);
        return;
      }
      // Chrome only feeds a remote WebRTC stream into WebAudio if a media element is also consuming it.
      const el = new Audio(); el.muted = true; el.srcObject = stream; void el.play().catch(() => {});
      peer.el = el;
      peer.src?.disconnect();
      peer.src = ctx.createMediaStreamSource(stream); peer.src.connect(gain);
    };
    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      if (s === "failed") pc.restartIce();
      peer.disconnectedAt = s === "disconnected" ? peer.disconnectedAt ?? Date.now() : null;
      this.emitPeer(id, { state: s });
    };
    this.emitPeer(id, { state: "new" });
    return peer;
  }
  private dropPeer(id: string) {
    this.states.delete(id);
    const p = this.peers.get(id); if (!p) return;
    this.closePeer(p); this.peers.delete(id);
    const peers = { ...this.view.peers }; delete peers[id];
    const speaking = { ...this.view.speaking }; delete speaking[id];
    const remote = { ...this.view.remote }; delete remote[id];
    this.emit({ peers, speaking, remote });
  }
  private closePeer(p: Peer) {
    p.pc.onnegotiationneeded = p.pc.onicecandidate = p.pc.ontrack = p.pc.onconnectionstatechange = null;
    p.pc.close(); p.src?.disconnect(); p.gain.disconnect(); p.analyser.disconnect();
    if (p.screenAudio) { p.screenAudio.src.disconnect(); p.screenAudio.gain.disconnect(); p.screenAudio.el.srcObject = null; }
    if (p.el) { p.el.srcObject = null; p.el = null; }
  }
  private emitPeer(id: string, patch: Partial<PeerStats>) {
    const prev = this.view.peers[id] ?? { state: "new", rttMs: null, lossPct: null, sendKbps: null, recvKbps: null, codec: null };
    this.emit({ peers: { ...this.view.peers, [id]: { ...prev, ...patch } } });
  }
  private async applyBitrate(p: Peer, bps: number) {
    if (!p.sender) return;
    const params = p.sender.getParameters();
    if (!params.encodings?.length) params.encodings = [{}];
    params.encodings[0]!.maxBitrate = bps;
    params.encodings[0]!.priority = "high"; params.encodings[0]!.networkPriority = "high";
    await p.sender.setParameters(params).catch(() => {});
  }

  // ---------- stats + adaptive bitrate ----------
  private async collectStats() {
    let rttSum = 0, rttN = 0, worstLoss = 0;
    for (const p of this.peers.values()) {
      if (p.disconnectedAt && Date.now() - p.disconnectedAt > 5000) { p.disconnectedAt = Date.now(); p.pc.restartIce(); }
      let stats: RTCStatsReport; try { stats = await p.pc.getStats(); } catch { continue; }
      let rtt: number | null = null, loss: number | null = null, sent = 0, recv = 0, codec: string | null = null;
      const vids: Record<string, VideoStat> = {};
      stats.forEach((r: any) => {
        if (r.type === "inbound-rtp" && r.kind === "video") {
          const sid = r.trackIdentifier ? [...p.streams.values()].find((ms) => ms.getVideoTracks().some((t) => t.id === r.trackIdentifier))?.id : undefined;
          const c = r.codecId && stats.get(r.codecId);
          const key = sid === p.media.screen ? "screen" : "cam";
          vids[key] = { width: r.frameWidth ?? 0, height: r.frameHeight ?? 0, fps: Math.round(r.framesPerSecond ?? 0), codec: c?.mimeType ?? null,
            impl: r.decoderImplementation ?? null, hw: typeof r.powerEfficientDecoder === "boolean" ? r.powerEfficientDecoder : null, kbps: null };
        }
        if (r.type === "candidate-pair" && r.nominated && r.state === "succeeded" && typeof r.currentRoundTripTime === "number") rtt = r.currentRoundTripTime * 1000;
        if (r.type === "remote-inbound-rtp" && r.kind === "audio" && typeof r.fractionLost === "number") loss = r.fractionLost * 100;
        if (r.type === "outbound-rtp" && r.kind === "audio") sent += r.bytesSent ?? 0;
        if (r.type === "inbound-rtp" && r.kind === "audio") { recv += r.bytesReceived ?? 0; const c = r.codecId && stats.get(r.codecId); if (c) codec = c.mimeType; }
      });
      const now = performance.now();
      let sendKbps: number | null = null, recvKbps: number | null = null;
      if (p.lastBytes) { const dt = (now - p.lastBytes.at) / 1000; sendKbps = Math.round(((sent - p.lastBytes.sent) * 8) / dt / 1000); recvKbps = Math.round(((recv - p.lastBytes.recv) * 8) / dt / 1000); }
      p.lastBytes = { sent, recv, at: now };
      if (rtt !== null) { rttSum += rtt; rttN++; }
      if (loss !== null) worstLoss = Math.max(worstLoss, loss);
      this.emitPeer(p.id, { rttMs: rtt === null ? null : Math.round(rtt), lossPct: loss === null ? null : Math.round(loss * 10) / 10, sendKbps, recvKbps, codec,
        inCam: p.media.cam ? vids.cam ?? null : null, inScreen: p.media.screen ? vids.screen ?? null : null });
    }
    // AIMD on the shared send bitrate: back off fast on loss, recover slowly.
    const { target, current } = this.view.bitrate;
    let next = current;
    if (worstLoss > 8) { next = Math.max(16000, Math.round(current * 0.7)); this.goodPeriods = 0; }
    else if (worstLoss < 2 && current < target && ++this.goodPeriods >= 3) { next = Math.min(target, Math.round(current * 1.15)); this.goodPeriods = 0; }
    if (next !== current) { for (const p of this.peers.values()) await this.applyBitrate(p, next); }
    this.emit({ pingMs: rttN ? Math.round(rttSum / rttN) : null, bitrate: { target, current: next } });
  }

  /** For tests and diagnostics: decoded level (dBFS, after per-user volume) of each peer's audio right now. */
  debugLevels() { const out: Record<string, number> = {}; for (const p of this.peers.values()) out[p.id] = Math.round(levelDb(p.analyser, this.buf)); return out; }
  /** For tests and diagnostics: raw stats from every peer connection. */
  async debugStats() {
    const out: Record<string, any[]> = {};
    for (const p of this.peers.values()) { const rs: any[] = []; (await p.pc.getStats()).forEach((r) => rs.push(r)); out[p.id] = rs; }
    return out;
  }
}

/** Mic level meter for the settings page (works without joining a channel). */
export async function micTest(constraints: MediaTrackConstraints, onLevel: (db: number) => void): Promise<() => void> {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });
  const ctx = new AudioContext(); const a = ctx.createAnalyser(); a.fftSize = 1024;
  ctx.createMediaStreamSource(stream).connect(a);
  const buf = new Float32Array(1024);
  const id = window.setInterval(() => onLevel(levelDb(a, buf)), 50);
  return () => { clearInterval(id); stream.getTracks().forEach((t) => t.stop()); void ctx.close(); };
}
